import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { Box, LinearProgress, Pagination, TextField } from '@mui/material';
import Select from 'antd/lib/select';
import { Button } from '@material-ui/core';

import OpportunitiesPageBanner from 'assets/pageBanners/opportunities.avif';
import { selectAccessibleUserLocations, selectUser } from 'entities/user/selectors';
import {
  OpportunityProvider,
  OpportunityType,
  useSearchOpportunitiesQuery,
} from 'generated/graphql';
import { useDebounce } from 'utils/useDebounce';
import { PageBannerContainer } from 'components/PageBannerContainer/PageBannerContainer';
import { Features } from 'utils/features';
import FormDateWithStartAndEnd from 'retired/FormDateWithStartAndEnd';
import { Privileges } from 'utils/privileges';
import FormLocationSelector from 'retired/FormLocationSelector';
import { useCategories } from 'entities/cause/api';

import { opportunityProviderNames } from './opportunityProviderNames';
import { OpportunitiesList } from './OpportunitiesList';
import 'antd/lib/select/style/index.less';
import { OpportunityPreview } from './OpportunityPreview/OpportunityPreview';
import { Opportunity } from './types';

const { Option } = Select;

const OpportunitiesPage = (): JSX.Element => {
  const history = useHistory();

  const [opportunityPreview, setOpportunityPreview] = useState<null | Opportunity>(null);
  const categories = useCategories();
  const [selectedProviders, setSelectedProviders] = useState<OpportunityProvider[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [types, setTypes] = useState<OpportunityType[]>([]);
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState<Date>();
  const [dateTo, setDateTo] = useState<Date>();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const locations = useSelector(selectAccessibleUserLocations);

  const user = useSelector(selectUser);

  if (
    user &&
    (!user?.hasFeature(Features.BROWSING_EXTERNAL_OPPORTUNITIES) ||
      (!user.hasPrivilege(Privileges.EVENT_MANAGE) &&
        !user.hasPrivilege(Privileges.PROJECT_MANAGE)))
  ) {
    history.replace('/');
  }

  const itemsPerPage = 24;
  const { data, loading, error } = useSearchOpportunitiesQuery({
    variables: {
      searchTerm: debouncedSearchTerm,
      offset: (page - 1) * itemsPerPage,
      limit: itemsPerPage,
      types,
      dateFrom: dateFrom?.toISOString(),
      dateTo: dateTo?.toISOString(),
      categories: selectedCategories.length > 0 ? selectedCategories : undefined,
      providers: selectedProviders.length > 0 ? selectedProviders : undefined,
      locations: selectedLocations.map((locId) => {
        const location = locations.get(locId);
        return {
          // map from [lat, long] to [long, lat]
          coordinates: [location.coordinates[1], location.coordinates[0]],
          radius: location.radius || 20,
        };
      }),
    },
    fetchPolicy: 'no-cache',
  });
  const opportunities = data?.searchOpportunities?.opportunities ?? [];

  const sourcingRulesButton = user?.hasFeature(Features.AUTO_SOURCE_DEEDS) &&
    user?.hasPrivilege(Privileges.AUTO_SOURCE_MANAGE) && (
      <div>
        <Button
          color="primary"
          variant="outlined"
          size="large"
          component={Link}
          to="/deed-sourcing"
        >
          Sourcing Rules
        </Button>
      </div>
    );

  const onDateFromChange = (date) => {
    setDateFrom(date);
    setPage(1);
  };
  const onDateToChange = (date) => {
    setDateTo(date);
    setPage(1);
  };
  const filters = (
    <Box mb={4} display="flex" alignItems="baseline">
      <TextField
        variant="standard"
        label="Search opportunities"
        placeholder=""
        value={searchTerm}
        style={{ width: 290 }}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(1);
        }}
      />

      <Select
        value={types}
        placeholder="Filter by type"
        mode="multiple"
        onChange={(newValue) => {
          setTypes(newValue);
          setPage(1);
        }}
        style={{
          width: 200,
          marginLeft: 30,
        }}
      >
        <Option value={OpportunityType.Event}>Event</Option>
        <Option value={OpportunityType.Project}>Project</Option>
      </Select>

      <FormDateWithStartAndEnd
        names={['dateFrom', 'dateTo']}
        style={{
          marginLeft: 30,
        }}
        placeholder={['Starting after', 'Ending before']}
        future
        dateFrom={{
          input: {
            onChange: onDateFromChange,
            value: dateFrom,
          },
        }}
        dateTo={{
          input: {
            onChange: onDateToChange,
            value: dateTo,
          },
        }}
      />
      <FormLocationSelector
        input={{
          onChange: (value: string[]): void => {
            setSelectedLocations(value);
            setPage(1);
          },
          value: selectedLocations,
        }}
        locations={locations}
        placeholder="Filter by location"
        style={{
          width: 200,
          marginLeft: 30,
        }}
      />
      <Select
        value={selectedCategories}
        placeholder="Filter by category"
        mode="multiple"
        onChange={(newValue) => {
          setSelectedCategories(newValue);
          setPage(1);
        }}
        style={{
          width: 200,
          marginLeft: 30,
        }}
      >
        {categories.map((c) => (
          <Option value={c.code} key={c.code}>
            {c.name}
          </Option>
        ))}
      </Select>
      <Select
        value={selectedProviders}
        placeholder="Filter by provider"
        mode="multiple"
        onChange={(newValue) => {
          setSelectedProviders(newValue);
          setPage(1);
        }}
        style={{
          width: 200,
          marginLeft: 30,
        }}
      >
        {Object.values(OpportunityProvider)
          .filter((providerId) => {
            // Goodera is now that weird thing in between an opportunity and just a deed, where it comes from an external provider, but has no actual opportunity counterpart
            if (providerId === OpportunityProvider.Goodera) {
              return false;
            }
            return !user?.organization.settings
              ?.get('disabledOpportunityProviders')
              ?.includes(providerId);
          })
          .map((providerId) => (
            <Option value={providerId} key={providerId}>
              {opportunityProviderNames[providerId]}
            </Option>
          ))}
      </Select>
    </Box>
  );

  if (opportunityPreview) {
    return (
      <>
        <Helmet title={opportunityPreview.name} />
        <OpportunityPreview
          opportunity={opportunityPreview}
          setOpportunityPreview={setOpportunityPreview}
        />
      </>
    );
  }

  return (
    <>
      <Helmet title="Volunteer opportunities" />
      <PageBannerContainer color="#DDDFFF" backgroundImage={OpportunitiesPageBanner}>
        <h1>Volunteer opportunities</h1>

        {sourcingRulesButton}
      </PageBannerContainer>
      {filters}
      {error ? (
        <div>There was an error loading this page. Please try again.</div>
      ) : loading ? (
        <LinearProgress />
      ) : (
        <>
          <OpportunitiesList
            opportunities={opportunities}
            loading={loading}
            setOpportunityPreview={setOpportunityPreview}
          />
          {(data?.searchOpportunities?.total ?? 0) > 0 && (
            <Box my={2} display="flex" justifyContent="center">
              <Pagination
                count={Math.ceil((data?.searchOpportunities?.total ?? 0) / itemsPerPage)}
                page={page}
                onChange={(_, pageNumber) => {
                  setPage(pageNumber);
                }}
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default OpportunitiesPage;
